import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import type { DashboardMode, CompetitorFilters, MyBrandFilters } from "../types/dashboard.types";
import {
  appliedStateToExploreSearch,
  exploreSearchEquals,
  EXPLORE_ROUTES,
  isExploreSearchInSyncWithApplied,
  parseExploreSearch,
  searchFromLocationHref,
  shouldHydrateStoreFromUrl,
  type ExploreSearch,
} from "../lib/explore-search";
import { resolveExploreFiltersForStore } from "../lib/resolve-explore-filters";
import { mergeExploreHydrationWithStore } from "../lib/merge-explore-hydration";
import { useDashboardFiltersStore } from "../stores/dashboardFilters.store";

export type UrlHydrationPhase = "pending" | "hydrated" | "store-driven";

export interface UseExploreSearchSyncOptions {
  activeTenantId: string | null;
  mode: DashboardMode;
  /** Draft filters — used for live URL sync and mode toggle while dirty. */
  competitorFilters: CompetitorFilters;
  myBrandFilters: MyBrandFilters;
  appliedCompetitorFilters: CompetitorFilters;
  appliedMyBrandFilters: MyBrandFilters;
  /** Dashboard global filters dirty (not overview local filters). */
  isDraftDirty: boolean;
  industries: string[];
  myIndustryBrandNames: string[];
  competitorBrandNames: string[];
  selectableAdAccountIds: string[];
  allowedMyBrandPlatformIds: string[];
  allowedCompetitorPlatformIds: string[];
  selectableProductCodes: string[];
  accountFallback: string[] | null;
  productFallback: {
    industryProducts: string[];
    products: string[] | null;
  };
  /** True after the competitor-industry options query succeeds, including an empty list. */
  competitorBrandsLoaded?: boolean;
  hydrateFromApplied: (payload: {
    mode: DashboardMode;
    competitorFilters: CompetitorFilters;
    myBrandFilters: MyBrandFilters;
  }) => void;
  /** Discard draft before hydrating from an external URL change (back/forward, paste). */
  onExternalUrlNavigation?: () => void;
  setMode: (mode: DashboardMode) => void;
}

export interface PushExploreUrlOptions {
  /** Serialize these values instead of the closure defaults. */
  modeOverride?: DashboardMode;
  competitorOverride?: CompetitorFilters;
  myBrandOverride?: MyBrandFilters;
  /** Bypass the deep-link pending guard (e.g. tenant switch canonicalization). */
  skipPendingHydrationGuard?: boolean;
}

export function useExploreSearchSync(options: UseExploreSearchSyncOptions) {
  const {
    activeTenantId,
    mode,
    competitorFilters,
    myBrandFilters,
    appliedCompetitorFilters,
    appliedMyBrandFilters,
    isDraftDirty,
    industries,
    myIndustryBrandNames,
    competitorBrandNames,
    selectableAdAccountIds,
    allowedMyBrandPlatformIds,
    allowedCompetitorPlatformIds,
    selectableProductCodes,
    accountFallback,
    productFallback,
    competitorBrandsLoaded = false,
    hydrateFromApplied,
    onExternalUrlNavigation,
    setMode,
  } = options;

  const navigate = useNavigate();
  const href = useRouterState({ select: (s) => s.location.href });
  const persistCustomWindow = useRouterState({
    select: (s) => EXPLORE_ROUTES.has(s.location.pathname),
  });
  const isExploreRoute = useRouterState({
    select: (s) => EXPLORE_ROUTES.has(s.location.pathname),
  });

  const rawSearchRecord = useMemo(() => searchFromLocationHref(href), [href]);
  const parsedSearch = useMemo(() => parseExploreSearch(rawSearchRecord), [rawSearchRecord]);

  const isPushingRef = useRef(false);
  const lastHydratedKeyRef = useRef<string | null>(null);
  const storeInitiatedNavigationRef = useRef(false);
  const productFallbackRef = useRef(productFallback);
  productFallbackRef.current = productFallback;
  const onExternalUrlNavigationRef = useRef(onExternalUrlNavigation);
  onExternalUrlNavigationRef.current = onExternalUrlNavigation;

  const [urlHydrationPhase, setUrlHydrationPhase] = useState<UrlHydrationPhase>("pending");

  const buildResolveContext = useCallback(
    () => ({
      industries,
      myIndustryBrandNames,
      competitorBrandNames,
      selectableAdAccountIds,
      allowedMyBrandPlatformIds,
      allowedCompetitorPlatformIds,
      selectableProductCodes,
      accountFallback,
      productFallback: productFallbackRef.current,
      competitorBrandsLoaded,
      persistCustomWindow,
    }),
    [
      industries,
      myIndustryBrandNames,
      competitorBrandNames,
      selectableAdAccountIds,
      allowedMyBrandPlatformIds,
      allowedCompetitorPlatformIds,
      selectableProductCodes,
      accountFallback,
      competitorBrandsLoaded,
      persistCustomWindow,
    ],
  );

  const navigateSearch = useCallback(
    (next: ExploreSearch, replace: boolean) => {
      if (!isExploreRoute) return;
      const current = parseExploreSearch(searchFromLocationHref(href));
      if (exploreSearchEquals(current, next)) return;
      isPushingRef.current = true;
      storeInitiatedNavigationRef.current = true;
      void navigate({ search: next as never, replace });
    },
    [navigate, href, isExploreRoute],
  );

  const replaceSearch = useCallback(
    (next: ExploreSearch) => navigateSearch(next, true),
    [navigateSearch],
  );

  const markStoreInitiatedNavigation = useCallback(() => {
    storeInitiatedNavigationRef.current = true;
    setUrlHydrationPhase("store-driven");
  }, []);

  const shouldDeferStoreToUrlPush = useCallback(() => {
    return urlHydrationPhase === "pending" && shouldHydrateStoreFromUrl(parsedSearch);
  }, [urlHydrationPhase, parsedSearch]);

  useEffect(() => {
    lastHydratedKeyRef.current = null;
    setUrlHydrationPhase("pending");
  }, [activeTenantId]);

  // Prune invalid URL values once authorized option lists are available.
  // Blocked while draft is dirty — option lists follow draft but URL may lag until Apply.
  useEffect(() => {
    if (!isExploreRoute || isDraftDirty) return;
    if (!shouldHydrateStoreFromUrl(parsedSearch)) return;
    const resolved = resolveExploreFiltersForStore(parsedSearch, buildResolveContext());
    if (resolved.searchCorrected) {
      replaceSearch(resolved.search);
    }
  }, [
    parsedSearch,
    isExploreRoute,
    isDraftDirty,
    buildResolveContext,
    replaceSearch,
    industries,
    myIndustryBrandNames,
    competitorBrandNames,
    selectableAdAccountIds,
    allowedMyBrandPlatformIds,
    allowedCompetitorPlatformIds,
    selectableProductCodes,
  ]);

  // URL → store (load, back/forward, deep link). Store-initiated replace updates are skipped.
  useEffect(() => {
    if (!isExploreRoute) return;

    const searchKey = JSON.stringify(parsedSearch);
    if (isPushingRef.current) {
      isPushingRef.current = false;
      storeInitiatedNavigationRef.current = false;
      lastHydratedKeyRef.current = searchKey;
      return;
    }
    if (searchKey === lastHydratedKeyRef.current) return;

    // Past here the URL changed outside the store (back/forward, paste, external link),
    // so a marker left over from a push that never navigated must not survive it.
    storeInitiatedNavigationRef.current = false;

    if (!shouldHydrateStoreFromUrl(parsedSearch)) {
      lastHydratedKeyRef.current = searchKey;
      setUrlHydrationPhase("hydrated");
      return;
    }

    // External navigation while editing: URL wins — discard draft then hydrate.
    if (isDraftDirty) {
      onExternalUrlNavigationRef.current?.();
    }

    const resolved = resolveExploreFiltersForStore(parsedSearch, buildResolveContext());

    const merged = mergeExploreHydrationWithStore(
      useDashboardFiltersStore.getState(),
      resolved,
    );
    hydrateFromApplied(merged);

    lastHydratedKeyRef.current = searchKey;
    setUrlHydrationPhase("hydrated");
  }, [parsedSearch, isExploreRoute, isDraftDirty, buildResolveContext, hydrateFromApplied]);

  const serializeToExploreSearch = useCallback(
    (
      effMode: DashboardMode,
      effCompetitor: CompetitorFilters,
      effMyBrand: MyBrandFilters,
    ) =>
      appliedStateToExploreSearch(effMode, effCompetitor, effMyBrand, {
        persistCustomWindow,
      }),
    [persistCustomWindow],
  );

  const pushExploreUrl = useCallback(
    (
      {
        modeOverride,
        competitorOverride,
        myBrandOverride,
        skipPendingHydrationGuard,
      }: PushExploreUrlOptions,
      options: { source: "draft" | "applied"; deferDuringPendingHydration?: boolean },
    ) => {
      if (!isExploreRoute) return;

      const deferPending =
        options.deferDuringPendingHydration !== false && !skipPendingHydrationGuard;
      if (deferPending && shouldDeferStoreToUrlPush()) {
        return;
      }

      const effMode = modeOverride ?? mode;
      const effCompetitor =
        competitorOverride ??
        (options.source === "draft" ? competitorFilters : appliedCompetitorFilters);
      const effMyBrand =
        myBrandOverride ??
        (options.source === "draft" ? myBrandFilters : appliedMyBrandFilters);

      const next = serializeToExploreSearch(effMode, effCompetitor, effMyBrand);
      const current = parseExploreSearch(searchFromLocationHref(href));
      if (exploreSearchEquals(current, next)) return;

      if (
        options.source === "applied" &&
        !storeInitiatedNavigationRef.current &&
        shouldHydrateStoreFromUrl(current) &&
        !isExploreSearchInSyncWithApplied(current, effMode, effCompetitor, effMyBrand, {
          persistCustomWindow,
        })
      ) {
        return;
      }

      replaceSearch(next);
      lastHydratedKeyRef.current = JSON.stringify(next);
    },
    [
      isExploreRoute,
      shouldDeferStoreToUrlPush,
      mode,
      competitorFilters,
      myBrandFilters,
      appliedCompetitorFilters,
      appliedMyBrandFilters,
      href,
      replaceSearch,
      serializeToExploreSearch,
      persistCustomWindow,
    ],
  );

  const pushDraftToUrl = useCallback(
    (overrides: PushExploreUrlOptions = {}) => {
      pushExploreUrl(overrides, { source: "draft" });
    },
    [pushExploreUrl],
  );

  const pushAppliedToUrl = useCallback(
    (overrides: PushExploreUrlOptions = {}) => {
      pushExploreUrl(overrides, { source: "applied", deferDuringPendingHydration: true });
    },
    [pushExploreUrl],
  );

  const handleModeChange = useCallback(
    (newMode: DashboardMode) => {
      markStoreInitiatedNavigation();
      setMode(newMode);

      const sourceCompetitor = isDraftDirty ? competitorFilters : appliedCompetitorFilters;
      const sourceMyBrand = isDraftDirty ? myBrandFilters : appliedMyBrandFilters;

      const next = serializeToExploreSearch(newMode, sourceCompetitor, sourceMyBrand);
      replaceSearch(next);
      lastHydratedKeyRef.current = JSON.stringify(next);
    },
    [
      isDraftDirty,
      competitorFilters,
      myBrandFilters,
      appliedCompetitorFilters,
      appliedMyBrandFilters,
      replaceSearch,
      setMode,
      markStoreInitiatedNavigation,
      serializeToExploreSearch,
    ],
  );

  return {
    handleModeChange,
    pushDraftToUrl,
    pushAppliedToUrl,
    markStoreInitiatedNavigation,
    parsedSearch,
    isExploreRoute,
    urlHydrationPhase,
  };
}
